import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useRole, VIEW_AS_OPTIONS } from '../context/RoleContext';
import { clearAccessToken, API_URL } from '../config/api';
import NotificationsCenter from './NotificationsCenter';
import MeetingAlert from './MeetingAlert';
import TeamChat from './TeamChat';
import BackButton from './BackButton';

// Super Admin Role Switcher Component
function RoleSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { isSuperAdmin, viewAsRole, switchViewAs, clearViewAs } = useRole();

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isSuperAdmin) return null;

  const currentView = viewAsRole || 'super_admin';
  const currentOption = VIEW_AS_OPTIONS.find(opt => opt.value === currentView) || VIEW_AS_OPTIONS[0];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
          viewAsRole 
            ? 'bg-purple-500 text-white hover:bg-purple-600'
            : (darkMode ? 'bg-yellow-900/50 text-yellow-400 border border-yellow-600' : 'bg-yellow-100 text-yellow-700 border border-yellow-300')
        }`}
        title="Switch Role View (Super Admin)"
      >
        <span>{currentOption.icon}</span>
        <span className="hidden sm:inline">
          {viewAsRole ? `Viewing as: ${currentOption.label}` : '👑 Super Admin'}
        </span>
        <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className={`absolute right-0 mt-2 w-56 rounded-xl shadow-2xl z-[100] overflow-hidden ${
          darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
          {/* Admin Panel Links */}
          <div className={`px-4 py-2 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Admin Panels
            </p>
          </div>
          <div className="py-1">
            <button
              onClick={() => { navigate('/super-admin'); setIsOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="text-lg">👑</span>
              <span>Super Admin Dashboard</span>
            </button>
            <button
              onClick={() => { navigate('/admin'); setIsOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="text-lg"></span>
              <span>Agency Admin Panel</span>
            </button>
          </div>
          
          {/* Role Switching */}
          <div className={`px-4 py-2 border-t border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Switch Role View
            </p>
          </div>
          <div className="py-1">
            {VIEW_AS_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  if (option.value === 'super_admin') {
                    clearViewAs();
                  } else {
                    switchViewAs(option.value);
                  }
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  currentView === option.value
                    ? (darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-50 text-purple-700')
                    : (darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50')
                }`}
              >
                <span className="text-lg">{option.icon}</span>
                <span>{option.label}</span>
                {currentView === option.value && (
                  <svg className="w-4 h-4 ml-auto text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>
          {viewAsRole && (
            <div className={`px-4 py-2 border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <button
                onClick={() => { clearViewAs(); setIsOpen(false); }}
                className={`w-full text-center text-xs py-1.5 rounded-lg ${
                  darkMode ? 'text-yellow-400 hover:bg-yellow-900/30' : 'text-yellow-600 hover:bg-yellow-50'
                }`}
              >
                ← Return to Super Admin View
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Personal Reminder Dropdown Component
function PersonalReminderDropdown({ isOpen, onClose, darkMode, toggleButtonRef, reminders, onComplete, onSnooze, onAddReminder }) {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newReminderTitle, setNewReminderTitle] = useState('');
  const [newReminderDate, setNewReminderDate] = useState('');
  const [newReminderTime, setNewReminderTime] = useState('');
  const [newReminderPriority, setNewReminderPriority] = useState('medium');

  const activeReminders = reminders.filter(r => !r.completed && !r.snoozedUntil);
  const snoozedReminders = reminders.filter(r => !r.completed && r.snoozedUntil);

  useEffect(() => {
    function handleClickOutside(event) {
      // Don't close if clicking the toggle button (let the button handle toggling)
      if (toggleButtonRef?.current && toggleButtonRef.current.contains(event.target)) {
        return;
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, toggleButtonRef]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return { bg: 'bg-red-500', text: 'text-white' };
      case 'high': return { bg: 'bg-orange-500', text: 'text-white' };
      case 'medium': return { bg: 'bg-blue-500', text: 'text-white' };
      case 'low': return { bg: 'bg-gray-500', text: 'text-white' };
      default: return { bg: 'bg-gray-500', text: 'text-white' };
    }
  };

  const handleAddReminder = () => {
    if (!newReminderTitle.trim()) return;
    onAddReminder({
      title: newReminderTitle,
      date: newReminderDate || new Date().toISOString().split('T')[0],
      time: newReminderTime || '09:00',
      priority: newReminderPriority,
    });
    setNewReminderTitle('');
    setNewReminderDate('');
    setNewReminderTime('');
    setNewReminderPriority('medium');
    setShowAddForm(false);
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={dropdownRef}
      className={`absolute right-0 mt-2 w-[380px] rounded-xl shadow-2xl z-[100] overflow-hidden ${
        darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
      }`}
    >
      {/* Header */}
      <div className={`px-4 py-3 flex items-center justify-between ${
        darkMode ? 'bg-gray-750 border-b border-gray-700' : 'bg-gray-50 border-b border-gray-100'
      }`}>
        <div className="flex items-center gap-2">
          <span className="text-green-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>My Reminders</h3>
          <span className={`px-1.5 py-0.5 text-xs rounded-full ${darkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-700'}`}>
            {activeReminders.length}
          </span>
        </div>
        <button
          onClick={onClose}
          className={`p-1 rounded-lg transition-colors ${
            darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Add New Reminder Section */}
      <div className={`px-4 py-3 ${darkMode ? 'bg-green-900/20 border-b border-green-800/30' : 'bg-green-50 border-b border-green-100'}`}>
        {!showAddForm ? (
          <button
            onClick={() => setShowAddForm(true)}
            className={`w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              darkMode 
                ? 'bg-green-600 hover:bg-green-700 text-white' 
                : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add New Reminder
          </button>
        ) : (
          <div className="space-y-2">
            <input
              type="text"
              placeholder="What do you need to remember?"
              value={newReminderTitle}
              onChange={(e) => setNewReminderTitle(e.target.value)}
              className={`w-full px-3 py-2 text-sm rounded-lg border ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              }`}
              autoFocus
            />
            <div className="flex gap-2">
              <input
                type="date"
                value={newReminderDate}
                onChange={(e) => setNewReminderDate(e.target.value)}
                className={`flex-1 px-2 py-1.5 text-xs rounded-lg border ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
              <input
                type="time"
                value={newReminderTime}
                onChange={(e) => setNewReminderTime(e.target.value)}
                className={`w-24 px-2 py-1.5 text-xs rounded-lg border ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
              <select
                value={newReminderPriority}
                onChange={(e) => setNewReminderPriority(e.target.value)}
                className={`w-24 px-2 py-1.5 text-xs rounded-lg border ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddReminder}
                className="flex-1 py-1.5 px-3 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded-lg transition-colors"
              >
                Save
              </button>
              <button
                onClick={() => { setShowAddForm(false); setNewReminderTitle(''); }}
                className={`py-1.5 px-3 text-xs font-medium rounded-lg transition-colors ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Reminders List */}
      <div className="max-h-72 overflow-y-auto">
        {activeReminders.length === 0 ? (
          <div className={`px-4 py-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <div className="text-3xl mb-2"></div>
            <p className="text-sm">All caught up!</p>
            <p className="text-xs mt-1">No pending reminders</p>
          </div>
        ) : (
          activeReminders.map(reminder => {
            const priorityColors = getPriorityColor(reminder.priority);
            return (
              <div
                key={reminder.id}
                className={`px-4 py-3 flex items-start gap-3 transition-colors ${
                  darkMode ? 'hover:bg-gray-700 border-b border-gray-700' : 'hover:bg-gray-50 border-b border-gray-100'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  darkMode ? 'bg-green-900/50' : 'bg-green-100'
                }`}>
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {reminder.title}
                    </p>
                    <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${priorityColors.bg} ${priorityColors.text}`}>
                      {reminder.priority}
                    </span>
                    {reminder.isUpcoming && (
                      <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-red-500 text-white animate-pulse">
                        Soon!
                      </span>
                    )}
                  </div>
                  <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {reminder.date} at {reminder.time}
                  </p>
                  {reminder.linkedDoc && (
                    <p className={`text-xs mt-1 flex items-center gap-1 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                      <span></span> {reminder.linkedDoc}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => onComplete(reminder.id)}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Done
                    </button>
                    <button
                      onClick={() => onSnooze(reminder.id, 60)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${
                        darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Snooze 1h
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className={`px-4 py-3 ${darkMode ? 'bg-gray-750 border-t border-gray-700' : 'bg-gray-50 border-t border-gray-100'}`}>
        <button 
          onClick={() => { onClose(); navigate('/calendar'); }}
          className={`w-full py-2 px-4 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 ${
            darkMode 
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          View Full Calendar
        </button>
      </div>
    </div>
  );
}

function TopBar({ title, user }) {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showTeamChat, setShowTeamChat] = useState(false);
  const [showReminderDropdown, setShowReminderDropdown] = useState(false);
  const [showRoleSwitcher, setShowRoleSwitcher] = useState(false);
  const { darkMode } = useTheme();
  const { isSuperAdmin, viewAsRole, switchViewAs, clearViewAs } = useRole();
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const reminderButtonRef = useRef(null);

  // Generate initial dates once (static for demo purposes)
  const getInitialReminders = () => {
    const now = Date.now();
    return [
      { id: 'r1', title: 'Review T4NG compliance docs', date: new Date(now + 15 * 60000).toISOString().split('T')[0], time: new Date(now + 15 * 60000).toTimeString().slice(0,5), priority: 'high', completed: false, linkedDoc: 'T4NG Proposal Draft v3.pdf' },
      { id: 'r2', title: 'Submit VISN 8 checklist', date: new Date(now + 2 * 60 * 60000).toISOString().split('T')[0], time: new Date(now + 2 * 60 * 60000).toTimeString().slice(0,5), priority: 'urgent', completed: false, linkedDoc: 'VISN 8 Compliance Checklist.xlsx' },
      { id: 'r3', title: 'Follow up with James Wilson', date: new Date(now + 24 * 60 * 60000).toISOString().split('T')[0], time: '10:30', priority: 'medium', completed: false, linkedDoc: null },
      { id: 'r4', title: 'Prepare for stakeholder meeting', date: new Date(now + 48 * 60 * 60000).toISOString().split('T')[0], time: '08:00', priority: 'high', completed: false, linkedDoc: null },
    ];
  };

  // Personal reminders state - lifted up from dropdown
  const [reminders, setReminders] = useState(getInitialReminders);

  // Check if any reminder is within 30 minutes
  const checkUpcomingReminders = useCallback(() => {
    const now = new Date();
    const thirtyMinutesFromNow = new Date(now.getTime() + 30 * 60000);
    
    return reminders.some(reminder => {
      if (reminder.completed || reminder.snoozedUntil) return false;
      
      // Check if snoozed until time has passed
      if (reminder.snoozedUntil && reminder.snoozedUntil > now.getTime()) {
        return false;
      }
      
      const reminderDateTime = new Date(`${reminder.date}T${reminder.time}`);
      return reminderDateTime >= now && reminderDateTime <= thirtyMinutesFromNow;
    });
  }, [reminders]);

  const [hasUpcomingReminder, setHasUpcomingReminder] = useState(false);

  // Update upcoming reminder status whenever reminders change
  useEffect(() => {
    const updateUpcoming = () => {
      setHasUpcomingReminder(checkUpcomingReminders());
    };
    
    updateUpcoming();
    // Check every minute
    const interval = setInterval(updateUpcoming, 60000);
    return () => clearInterval(interval);
  }, [checkUpcomingReminders]);

  // Mark reminders with "isUpcoming" flag for display
  const remindersWithStatus = reminders.map(reminder => {
    if (reminder.completed || reminder.snoozedUntil) return reminder;
    
    const now = new Date();
    const thirtyMinutesFromNow = new Date(now.getTime() + 30 * 60000);
    const reminderDateTime = new Date(`${reminder.date}T${reminder.time}`);
    const isUpcoming = reminderDateTime >= now && reminderDateTime <= thirtyMinutesFromNow;
    
    return { ...reminder, isUpcoming };
  });

  // Handler functions
  const handleCompleteReminder = (id) => {
    setReminders(prev => prev.map(r => r.id === id ? { ...r, completed: true } : r));
  };

  const handleSnoozeReminder = (id, minutes) => {
    setReminders(prev => prev.map(r => r.id === id ? { ...r, snoozedUntil: Date.now() + minutes * 60000 } : r));
  };

  const handleAddReminder = (newReminder) => {
    setReminders(prev => [{
      id: `r${Date.now()}`,
      ...newReminder,
      completed: false,
      linkedDoc: null
    }, ...prev]);
  };

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get initials from full name (e.g., "Sarah Miller" -> "SM")
  const getInitials = () => {
    if (user?.name || user?.full_name) {
      const names = (user.name || user.full_name).split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return names[0][0].toUpperCase();
    }
    return user?.username?.[0]?.toUpperCase() || 'U';
  };

  const handleNavigation = (path) => {
    setShowUserMenu(false);
    navigate(path);
  };

  return (
    <header 
      className={`px-6 py-4 transition-colors duration-300 ${
        darkMode ? 'bg-gray-800 border-b border-gray-700' : 'bg-white border-b border-gray-200'
      }`}
      role="banner"
      aria-label="Page header"
    >
      <div className="flex items-center justify-between">
        {/* Left side: Back Button + Title */}
        <div className="flex items-center gap-2">
          <BackButton />
          <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h1>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Meeting Alert - appears when meeting is upcoming (leftmost) */}
          <MeetingAlert />
          
          {/* Unified Notification Bell - includes alerts + messages */}
          <NotificationsCenter onOpenChat={() => setShowTeamChat(!showTeamChat)} />

          {/* Personal Reminder Badge - green box with reminder+ */}
          <div className="relative">
            <button
              ref={reminderButtonRef}
              onClick={() => setShowReminderDropdown(!showReminderDropdown)}
              className={`relative flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-medium transition-all overflow-hidden ${
                showReminderDropdown
                  ? 'bg-green-600 text-white ring-2 ring-green-400'
                  : 'bg-green-500 text-white hover:bg-green-600'
              }`}
              title="Personal Reminders"
              aria-label="Personal reminders"
              aria-expanded={showReminderDropdown}
            >
              {/* Shimmer sweep effect - only when there's an upcoming reminder */}
              {hasUpcomingReminder && !showReminderDropdown && (
                <span className="shimmer-sweep"></span>
              )}
              <span className="relative z-10">reminder</span>
              <span className="relative z-10 font-bold">+</span>
            </button>
            
            {/* CSS for shimmer sweep animation */}
            <style>{`
              .shimmer-sweep {
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(
                  120deg,
                  transparent 0%,
                  transparent 40%,
                  rgba(255, 255, 255, 0.4) 50%,
                  transparent 60%,
                  transparent 100%
                );
                animation: shimmer 5s ease-in-out infinite;
              }
              
              @keyframes shimmer {
                0% {
                  left: -100%;
                }
                100% {
                  left: 100%;
                }
              }
            `}</style>
            
            {/* Personal Reminder Dropdown */}
            <PersonalReminderDropdown 
              isOpen={showReminderDropdown} 
              onClose={() => setShowReminderDropdown(false)}
              darkMode={darkMode}
              toggleButtonRef={reminderButtonRef}
              reminders={remindersWithStatus}
              onComplete={handleCompleteReminder}
              onSnooze={handleSnoozeReminder}
              onAddReminder={handleAddReminder}
            />
          </div>

          {/* Team Chat Popup */}
          <TeamChat isOpen={showTeamChat} onClose={() => setShowTeamChat(false)} />

          {/* User Menu (rightmost) */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className={`flex items-center gap-2 p-1.5 rounded-lg transition-all ${
                darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              } ${showUserMenu ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}`}
            >
              <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-semibold shadow-sm">
                {getInitials()}
              </div>
              <div className="hidden sm:block text-left">
                <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {(user?.name || user?.full_name)?.split(' ').map(n => n.charAt(0).toUpperCase() + n.slice(1).toLowerCase()).join(' ') || 'User'}
                </div>
                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {user?.title || (user?.role === 'super_admin' ? 'Contracting Officer' : user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())) || 'User'}
                </div>
              </div>
              <svg 
                className={`w-4 h-4 transition-transform duration-200 ${darkMode ? 'text-gray-400' : 'text-gray-600'} ${showUserMenu ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showUserMenu && (
              <div className={`absolute right-0 mt-2 w-64 rounded-xl shadow-xl py-2 z-50 animate-fade-in-up ${
                darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
                {/* User Info Header */}
                <div className={`px-4 py-3 ${darkMode ? 'border-b border-gray-700' : 'border-b border-gray-100'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-lg font-semibold shadow-md">
                      {getInitials()}
                    </div>
                    <div>
                      <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {(user?.name || user?.full_name)?.split(' ').map(n => n.charAt(0).toUpperCase() + n.slice(1).toLowerCase()).join(' ') || 'User'}
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {user?.email || 'user@acusightpro.gov'}
                      </div>
                      <div className={`text-xs mt-0.5 px-2 py-0.5 rounded-full inline-block ${
                        darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {user?.organization || 'VA'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  {/* Switch Role - Available to all users */}
                  <div className="relative">
                    <button 
                      onClick={() => setShowRoleSwitcher(!showRoleSwitcher)}
                      className={`w-full px-4 py-2.5 text-sm text-left flex items-center justify-between transition-colors ${
                        isSuperAdmin
                          ? (darkMode ? 'text-yellow-400 hover:bg-yellow-900/30' : 'text-yellow-700 hover:bg-yellow-50')
                          : (darkMode ? 'text-blue-400 hover:bg-blue-900/30' : 'text-blue-700 hover:bg-blue-50')
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <span>{isSuperAdmin ? '👑' : ''}</span>
                        <span>Switch Role</span>
                      </span>
                      <svg className={`w-4 h-4 transition-transform ${showRoleSwitcher ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    
                    {/* Role Switcher Submenu */}
                      {showRoleSwitcher && (
                        <div className={`mx-2 mb-2 rounded-lg overflow-hidden ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-100'
                        }`}>
                          {VIEW_AS_OPTIONS
                            .filter(option => {
                              // Only super admins can see Super Admin and Agency Admin options
                              if (option.value === 'super_admin' || option.value === 'admin') {
                                return isSuperAdmin;
                              }
                              return true;
                            })
                            .map((option) => {
                            // For non-super admins, show their current job_role as active
                            // For super admins, show viewAsRole or 'super_admin' as active
                            const userJobRole = user?.job_role || 'co';
                            const currentView = isSuperAdmin 
                              ? (viewAsRole || 'super_admin')
                              : (viewAsRole || userJobRole);
                            const isActive = currentView === option.value;
                            return (
                              <button
                                key={option.value}
                                onClick={() => {
                                  if (option.value === 'super_admin') {
                                    clearViewAs();
                                    // Navigate to Super Admin Dashboard
                                    navigate('/super-admin');
                                  } else if (option.value === 'admin') {
                                    switchViewAs(option.value);
                                    navigate('/admin');
                                  } else {
                                    switchViewAs(option.value);
                                    // Navigate to main dashboard for other roles
                                    navigate('/');
                                  }
                                  setShowRoleSwitcher(false);
                                  setShowUserMenu(false);
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                                  isActive
                                    ? (darkMode ? 'bg-purple-600 text-white' : 'bg-purple-500 text-white')
                                    : (darkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-200')
                                }`}
                              >
                                <span>{option.icon}</span>
                                <span>{option.label}</span>
                                {isActive && (
                                  <svg className="w-4 h-4 ml-auto text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </button>
                            );
                          })}
                          
                          {/* Divider and Admin Panel Links - Only for Super Admins */}
                          {isSuperAdmin && (
                            <div className={`border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                              <button
                                onClick={() => { handleNavigation('/super-admin'); setShowRoleSwitcher(false); }}
                                className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                                  darkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                <span>👑</span>
                                <span>Super Admin Dashboard</span>
                              </button>
                              <button
                                onClick={() => { handleNavigation('/admin'); setShowRoleSwitcher(false); }}
                                className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                                  darkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                <span></span>
                                <span>Agency Admin Panel</span>
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  
                  {/* Admin Panel - Only for admins (non-super) */}
                  {user?.role === 'admin' && !isSuperAdmin && (
                    <button 
                      onClick={() => handleNavigation('/admin')}
                      className={`w-full px-4 py-2.5 text-sm text-left flex items-center gap-3 transition-colors ${
                        darkMode ? 'text-purple-400 hover:bg-purple-900/30' : 'text-purple-700 hover:bg-purple-50'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Admin Panel
                    </button>
                  )}
                  <button 
                    onClick={() => handleNavigation('/settings?section=account')}
                    className={`w-full px-4 py-2.5 text-sm text-left flex items-center gap-3 transition-colors ${
                      darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    My Profile
                  </button>
                  <button 
                    onClick={() => handleNavigation('/settings')}
                    className={`w-full px-4 py-2.5 text-sm text-left flex items-center gap-3 transition-colors ${
                      darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Settings
                  </button>
                  <button 
                    onClick={() => handleNavigation('/help')}
                    className={`w-full px-4 py-2.5 text-sm text-left flex items-center gap-3 transition-colors ${
                      darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Help & Support
                  </button>
                </div>

                {/* Logout */}
                <div className={`mt-1 pt-1 ${darkMode ? 'border-t border-gray-700' : 'border-t border-gray-100'}`}>
                  <button 
                    onClick={async () => {
                      setShowUserMenu(false);
                      // Call logout endpoint to clear HttpOnly cookie
                      try {
                        const token = localStorage.getItem('asp_token');
                        if (token) {
                          await fetch(`${API_URL}/auth/logout`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}` },
                            credentials: 'include'
                          });
                        }
                      } catch (e) {
                        
                      }
                      // Clear all auth tokens and state
                      clearAccessToken();
                      localStorage.removeItem('asp_demo_access');
                      localStorage.removeItem('asp_view_as');
                      sessionStorage.removeItem('asp_demo_access');
                      // Force navigation to login
                      window.location.href = '/login';
                    }}
                    className={`w-full px-4 py-2.5 text-sm text-left flex items-center gap-3 transition-colors ${
                      darkMode ? 'text-red-400 hover:bg-red-900/20' : 'text-red-600 hover:bg-red-50'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default TopBar;